import { Extension, Mark } from '@tiptap/core';
/**
 * De nodes die een `ychange`-attribuut kunnen dragen.
 *
 * Elk element-nodetype dat in een rich-text-veld kan voorkomen hoort hier te staan, want
 * y-tiptap zet het attribuut op wat hij tegenkomt en niet op wat wij hebben opgesomd. Een
 * ontbrekend type is valkuil 2, dus de lijst wordt niet met de hand bijgehouden: er staat een
 * test op die hem vergelijkt met de nodetypes van een echte editor en luid valt bij verschil.
 */
export const YCHANGE_NODES = [
    'paragraph',
    'heading',
    'blockquote',
    'codeBlock',
    'bulletList',
    'orderedList',
    'listItem',
    'horizontalRule',
    'hardBreak',
    // `figuur` en niet `image`: onze figuur is een verwijzing naar de documentopslag en geen
    // afbeelding in het document (valkuil "een afbeelding in rich text is een verwijzing").
    'figuur',
    'referentie',
    'inlineMath',
    'blockMath',
    'table',
    'tableRow',
    'tableHeader',
    'tableCell',
    // Batch 2 van de knoppariteit met CitatenPOC's balk (eisenboek 12.9): de enige van de vier
    // nieuwe extensies met een eigen knooptype, gevonden via deze test zelf (test-eerst-laten-
    // falen): subscript/superscript/highlight zijn marks, text-align is een attribuut op een
    // bestaande knoop, geen van drie hoort hier dus bij.
    'taskList',
    'taskItem',
];
/**
 * Wat er van een markering te tekenen valt.
 *
 * Drie gevallen, en het derde is de reden dat deze functie bestaat.
 *
 * Toegevoegd met een auteur: de achtergrond of de rand in zijn kleur. Verwijderd:
 * doorgehaald, want dat de tekst weg is, is informatie ook zonder auteur; y-tiptap vraagt bij
 * verwijderde inhoud wie hem weghaalde en dat houdt de pod niet bij (`apps/runtime/src/auteurs.ts`),
 * dus daar staat meestal geen naam. Toegevoegd **zonder** auteur: niets tekenen.
 *
 * Dat laatste is geen versimpeling maar precies wat 6.9 wil. Een generatie begint bij een
 * revisie, dus de inhoud die de pod erin zaait staat er al voordat iemand iets typt, en die
 * zaaitransactie draagt geen auteur. Zonder deze regel zou "wie schreef wat" de hele
 * bestaande tekst als nieuw en naamloos markeren; met deze regel blijft precies staan wat
 * deze generatie is geschreven, met de auteur erbij. De nulsnapshot van de generatie zou
 * hetzelfde bereiken, maar die staat in de opslag en heeft nog geen route naar de browser;
 * die komt met de versielijst in M6.
 */
export function markerOf(change) {
    if (change === null || change === undefined || change.type === null)
        return 'geen';
    if (change.type === 'removed')
        return 'verwijderd';
    return change.user === null ? 'geen' : 'toegevoegd';
}
/** De attributen die de markering aan de DOM meegeeft, of niets. */
function attributesOf(change, kind) {
    const marker = markerOf(change);
    if (marker === 'geen')
        return {};
    const author = change?.user ?? '';
    const colorPair = change?.color;
    return {
        class: `ef-attributie ef-attributie--${kind} ef-attributie--${marker}`,
        ...(author === '' ? {} : { 'data-auteur': author }),
        // De kleur van de auteur als custom property, zodat de stylesheet bepaalt waar hij
        // terechtkomt (achtergrond, rand, doorhaling) en de opmaak niet in TypeScript staat.
        ...(colorPair === null || colorPair === undefined
            ? {}
            : { style: `--ef-attributie-licht: ${colorPair.light}; --ef-attributie-donker: ${colorPair.dark};` }),
    };
}
/**
 * Tekst-attributie: de mark die y-tiptap per auteur op tekst zet.
 *
 * Niet door de invuller te maken: er is geen commando en geen sneltoets. Alleen de
 * snapshot-render zet hem.
 */
export const YChangeMark = Mark.create({
    name: 'ychange',
    inclusive: false,
    excludes: '',
    addAttributes() {
        return {
            user: { default: null },
            type: { default: null },
            color: { default: null },
        };
    },
    parseHTML() {
        return [{ tag: 'ychange' }];
    },
    renderHTML({ mark }) {
        const change = mark.attrs;
        const attributes = attributesOf(change, 'tekst');
        // Geen markering betekent geen extra element om de tekst heen, maar de tag moet er wel
        // zijn: ProseMirror verwacht van een mark een renderuitkomst.
        return ['ychange', attributes, 0];
    },
});
/**
 * Node-attributie: een globaal `ychange`-attribuut op elk element-nodetype.
 *
 * Zo is per alinea, kop of lijstitem zichtbaar wie hem schreef, naast de achtergrond die de
 * mark op de tekst zet.
 */
export const YChangeAttribute = Extension.create({
    name: 'ychangeAttribuut',
    addGlobalAttributes() {
        return [
            {
                types: [...YCHANGE_NODES],
                attributes: {
                    ychange: {
                        default: null,
                        // Niet naar het opgeslagen document schrijven: dit is alleen render. y-tiptap
                        // slaat de sleutel bij het terugschrijven zelf ook over, en deze regel houdt hem
                        // ook uit een geplakt fragment.
                        parseHTML: () => null,
                        keepOnSplit: false,
                        renderHTML: (attributes) => attributesOf(attributes.ychange, 'node'),
                    },
                },
            },
        ];
    },
});
//# sourceMappingURL=ychange.js.map